import type { SessionAttendee } from '@sunrise-ui/api/events/api';
import { useMemo } from 'react';
import { useBulkGetProfiles } from '../queries/profiles';
import {
  useGetBulkSessionData,
  useGetSessionsByEventId,
} from '../queries/sessions';
import { SessionRole } from '../utils/sessions';

export type SpeakerModeratorOption = {
  label: string;
  value: string;
};

export type UseSpeakerModeratorsResult = {
  speakerModeratorOptions: SpeakerModeratorOption[];
  isLoading: boolean;
  isError: boolean;
};

/**
 * Hook to fetch and return all speakers and moderators for an event.
 * This provides a self-contained data source for speaker filtering components.
 *
 * @param eventId - The event ID to fetch speakers/moderators for
 * @returns Object containing speaker/moderator options, loading state, and error state
 */
export function useSpeakerModerators(
  eventId: string,
): UseSpeakerModeratorsResult {
  // Get all sessions for the event
  const {
    data: sessions,
    isLoading: isLoadingSessions,
    isError: isSessionsError,
  } = useGetSessionsByEventId(eventId);

  // Extract session IDs for bulk data fetching
  const sessionIds = useMemo(() => {
    return sessions?.map((session) => session.id).filter(Boolean) || [];
  }, [sessions]);

  // Fetch bulk session data (includes attendees with roles)
  const {
    data: bulkSessionData,
    isLoading: isLoadingBulkData,
    isError: isBulkDataError,
  } = useGetBulkSessionData({
    sessionIds,
  });

  // Extract all user IDs from bulk session data for profile fetching
  const user_ids = useMemo(() => {
    if (!bulkSessionData?.session_data) return [];

    return (
      bulkSessionData.session_data
        ?.flatMap(
          (sessionData) =>
            sessionData.data.attendees?.map(
              (attendee: SessionAttendee) => attendee.user_id,
            ) || [],
        )
        .filter(Boolean) || []
    );
  }, [bulkSessionData]);

  // Fetch profiles for all users
  const {
    data: profilesData,
    isLoading: isLoadingProfiles,
    isError: isProfilesError,
  } = useBulkGetProfiles(user_ids);

  // Process data to extract speaker/moderator options
  const speakerModeratorOptions = useMemo(() => {
    if (!bulkSessionData?.session_data || !profilesData) return [];

    const speakerModeratorIds = new Set<string>();

    // Extract user IDs for all speakers and moderators from ALL sessions in the event
    bulkSessionData.session_data.forEach((sessionData) => {
      const { attendees } = sessionData.data;
      if (attendees) {
        attendees.forEach((attendee: SessionAttendee) => {
          if (
            (attendee.role === SessionRole.SPEAKER ||
              attendee.role === SessionRole.MODERATOR) &&
            attendee.user_id
          ) {
            speakerModeratorIds.add(attendee.user_id);
          }
        });
      }
    });

    // Map user IDs to profile data for dropdown options
    return Array.from(speakerModeratorIds)
      .map((userId) => {
        const profile = profilesData.find((p) => p.user_id === userId);
        if (profile) {
          const { first_name, last_name } = profile;
          return {
            label:
              `${first_name || ''} ${last_name || ''}`.trim() ||
              'Unknown Speaker',
            value: userId,
          };
        }
        return null;
      })
      .filter(Boolean)
      .sort((a, b) =>
        a!.label.localeCompare(b!.label),
      ) as SpeakerModeratorOption[];
  }, [bulkSessionData, profilesData]);

  // Combine loading states
  const isLoading = isLoadingSessions || isLoadingBulkData || isLoadingProfiles;
  const isError = isSessionsError || isBulkDataError || isProfilesError;

  return {
    speakerModeratorOptions,
    isLoading,
    isError,
  };
}
